/**
 * O formulário de uma trilha — a tela que faltava para "Sua vez" significar alguma coisa.
 *
 * Quatro decisões desta tela vêm da medição dos 581 kickoffs, e nenhuma delas é de estilo:
 *
 * · **O servidor manda no que aparece.** Cada salvamento devolve o formulário inteiro
 *   reavaliado, e é ele que passa a valer: responder uma pergunta-porteira muda o que está
 *   visível, o que é obrigatório e o que o motor sugere por herança. Se esta tela recalculasse
 *   isso, a regra voltaria a viver em dois lugares — que é como 44 de 44 clientes acabaram
 *   respondendo uma pergunta que valia para 32.
 *
 * · **O rascunho salva sozinho.** A tela promete "pode parar e voltar"; um botão "salvar" que a
 *   pessoa esquece de clicar transforma essa promessa em perda de trabalho. O que se digita é
 *   gravado ao sair do campo e por tempo, e o que ainda não foi gravado aparece dito na tela.
 *
 * · **Enviar congela, e nada desfaz.** Não existe rota, serviço nem permissão de reabertura em
 *   lugar nenhum do portal — nem para o cliente, nem para o consultor. Por isso o envio pede
 *   confirmação explícita em vez de acontecer no primeiro clique, e por isso os textos não
 *   prometem que alguém reabre: prometer o que o portal não faz é combinar um e-mail que termina
 *   em "não dá".
 *
 * · **A recusa aparece no campo.** O `422` do servidor traz a lista completa do que falta; ela
 *   vira mensagem em cada pergunta, e não uma faixa vermelha genérica no topo que obriga a
 *   pessoa a caçar o campo.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { Aviso, Botao, Cartao, Chip } from "../componentes/ui";
import { BarraDeProgresso, PerguntaNoFormulario, ancoraDaPergunta } from "../componentes/kickoff";
import { ErroDaApi } from "../nucleo/api";
import { useTitulo } from "../nucleo/titulo";
import { formatarData } from "../nucleo/implantacoes";
import {
  CAMINHO_DOS_FORMULARIOS,
  CHIP_DO_ESTADO,
  ROTULO_DO_ESTADO,
  ROTULO_DO_MOTIVO,
  errosPorPergunta,
  useAnexar,
  useEnviarTrilha,
  useRemoverAnexo,
  useSalvarRascunho,
  useTrilha,
  useTrilhas,
  type FormularioDaTrilha,
  type ValorDaPergunta,
} from "../nucleo/kickoff";

/* Tempo entre a última tecla e a gravação. Curto o bastante para que fechar a aba raramente
   custe algo, longo o bastante para não mandar uma requisição por caractere digitado. Sair do
   campo grava na hora, sem esperar este relógio. */
const ATRASO_DO_SALVAMENTO = 1200;

export function RespostaDaTrilha() {
  const { trilha: parametro } = useParams();
  const chave = parametro ?? "";
  const consulta = useTrilha(chave);
  const dados = consulta.data;
  const formulario = dados?.disponivel ? dados.formulario : null;

  useTitulo(formulario?.nome ?? "Formulário");

  if (consulta.isPending) {
    return (
      <p className="lead" role="status">
        Carregando o formulário…
      </p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroDaApi
        ? consulta.error.message
        : "Não foi possível carregar este formulário agora. Tente de novo em instantes.";
    return (
      <>
        <h1 className="titulo-pagina">Formulário</h1>
        <Aviso tom="erro" data-testid="falha-da-trilha">
          {mensagem}
        </Aviso>
        <p className="nota-rodape mt-4">
          <Link to={CAMINHO_DOS_FORMULARIOS}>Ver os formulários liberados para você</Link>
        </p>
      </>
    );
  }

  if (!formulario) {
    return <TrilhaIndisponivel motivo={dados?.disponivel === false ? dados.motivo : ""} />;
  }

  // `key` amarra o estado local (rascunho pendente, confirmação de envio) à trilha aberta: trocar
  // de trilha pela URL sem remontar carregaria as pendências de uma para dentro da outra.
  return <Formulario key={formulario.chave} chave={chave} formulario={formulario} />;
}

/**
 * A trilha que o servidor não reconhece para esta implantação.
 *
 * A infraestrutura era o caso concreto disto até a v2 do catálogo: a v1 nascia não publicada
 * porque o inventário comprovava 4 campos, e publicar meia trilha faz quem responde achar que
 * respondeu tudo. Agora ela está publicada e o que sobra aqui são as trilhas de módulo que o
 * cliente não contratou — e o `catalogo_digital`, que não tem questionário no catálogo v1.
 *
 * A tela continua existindo porque o 404 seco é a pior resposta possível para quem chegou aqui
 * clicando num card que dizia "sua vez": a explicação vem do servidor, que é onde ela está
 * escrita para quem está do outro lado.
 */
function TrilhaIndisponivel({ motivo }: { motivo: string }) {
  return (
    <>
      <h1 className="titulo-pagina">Este formulário ainda não está aberto</h1>
      <Cartao>
        <Aviso tom="atencao" data-testid="trilha-indisponivel">
          {motivo ||
            "Este formulário não faz parte da sua implantação. Veja abaixo os que foram liberados."}
        </Aviso>
        <p className="cartao__texto mt-4">
          Enquanto ele não abre, quem conduz esta parte é o consultor responsável pela sua
          implantação — nada fica parado esperando por você aqui.
        </p>
        <div className="mt-4">
          <Link to={CAMINHO_DOS_FORMULARIOS} className="botao botao--fantasma">
            Ver os meus formulários
          </Link>
        </div>
      </Cartao>
    </>
  );
}

function Formulario({
  chave,
  formulario,
}: {
  chave: string;
  formulario: FormularioDaTrilha;
}) {
  const trilhas = useTrilhas();
  const salvar = useSalvarRascunho(chave);
  const enviar = useEnviarTrilha(chave);
  const navegar = useNavigate();
  const anexar = useAnexar(chave);
  const remover = useRemoverAnexo(chave);

  /* O que foi digitado e ainda não foi gravado. É a única parte do formulário que esta tela
     possui: todo o resto (visibilidade, progresso, impedimento, sugestão) vem do servidor. */
  const [pendentes, setPendentes] = useState<Map<string, ValorDaPergunta | null>>(new Map());
  const [errosDoServidor, setErrosDoServidor] = useState<Record<string, string>>({});
  const [confirmandoEnvio, setConfirmandoEnvio] = useState(false);
  const [enviando, setEnviando] = useState(false);
  /* Ninguém erra por ter acabado de chegar. Enquanto a pessoa não tentou enviar, o que falta é
     estado neutro — some o vermelho da abertura e as 26 repetições de "obrigatória e ainda não
     respondida" que faziam o formulário parecer quebrado antes da primeira resposta. Ver o 3.3
     dos ajustes da fase 1. */
  const [tentouEnviar, setTentouEnviar] = useState(false);
  /* Uma recusa do servidor pausa o salvamento automático até a pessoa mexer em algo. Sem isso o
     relógio reenviaria o mesmo rascunho inválido a cada 1,2 s, para sempre. */
  const [pausado, setPausado] = useState(false);

  /* O atalho da notificação "pendência devolvida" chega com `#pergunta-<id>`: a pessoa cai NA
     pergunta, não no topo do formulário. O bloco só existe depois que o formulário carrega, e
     este componente só monta com ele pronto. */
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash.startsWith(`#${ancoraDaPergunta("")}`)) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ block: "start" });
  }, [hash]);

  const salvando = salvar.isPending;
  const gravarRascunho = salvar.mutateAsync;
  const enviarTrilha = enviar.mutateAsync;

  const gravar = useCallback(
    async (lote: Map<string, ValorDaPergunta | null>): Promise<boolean> => {
      if (lote.size === 0) return true;
      try {
        await gravarRascunho(
          [...lote].map(([pergunta_id, valor]) => ({ pergunta_id, valor })),
        );
        setErrosDoServidor({});
        setPendentes((atuais) => {
          const restantes = new Map(atuais);
          // Só sai da fila o que continua igual ao que foi enviado: se a pessoa digitou de novo
          // enquanto a requisição estava no ar, a alteração nova continua pendente.
          for (const [id, valor] of lote) {
            if (restantes.get(id) === valor) restantes.delete(id);
          }
          return restantes;
        });
        return true;
      } catch (causa) {
        setErrosDoServidor(errosPorPergunta(causa));
        setPausado(true);
        return false;
      }
    },
    [gravarRascunho],
  );

  useEffect(() => {
    if (pendentes.size === 0 || pausado || salvando) return;
    const relogio = setTimeout(() => {
      void gravar(pendentes);
    }, ATRASO_DO_SALVAMENTO);
    return () => clearTimeout(relogio);
  }, [pendentes, pausado, salvando, gravar]);

  function aoMudar(perguntaId: string, valor: ValorDaPergunta | null) {
    setPausado(false);
    setErrosDoServidor((atuais) => {
      if (!(perguntaId in atuais)) return atuais;
      const restantes = { ...atuais };
      delete restantes[perguntaId];
      return restantes;
    });
    setPendentes((atuais) => new Map(atuais).set(perguntaId, valor));
  }

  async function confirmarEnvio() {
    /* A caixa fica aberta, com o botão em "Enviando…", até o servidor responder: fechá-la antes
       tirava da tela o único sinal de que o envio estava em curso. Ela fecha ao fim — na
       recusa, para que o erro apareça no campo que o causou; no sucesso, porque o formulário
       passa a "enviado". */
    setEnviando(true);
    try {
      // O que está na tela precisa estar no banco antes de o servidor julgar se falta alguma coisa.
      if (!(await gravar(pendentes))) return;
      try {
        await enviarTrilha();
        setErrosDoServidor({});
        // Enviar é o ato final da trilha; o rascunho automático acima NÃO sai da tela.
        void navegar(CAMINHO_DOS_FORMULARIOS, { state: { avisoDeSucesso: "Trilha enviada." } });
      } catch (causa) {
        // O servidor pode ver o que o retrato desta tela não via — outra aba respondendo a mesma
        // trilha, por exemplo. A lista que ele devolve manda.
        setErrosDoServidor(errosPorPergunta(causa));
      }
    } finally {
      setEnviando(false);
      setConfirmandoEnvio(false);
    }
  }

  const nomesDasTrilhas = useMemo(() => {
    const nomes: Record<string, string> = {};
    for (const trilha of trilhas.data?.trilhas ?? []) nomes[trilha.chave] = trilha.nome;
    return nomes;
  }, [trilhas.data]);

  const somenteLeitura = !formulario.pode_responder;
  const congelada = formulario.sessao.estado === "enviada";
  const ocupado = salvando || enviar.isPending || anexar.isPending || remover.isPending;
  /* Quem tem 37 perguntas na tela precisa ver o que falta enquanto responde — a descoberta só no
     clique final é o que produz a resposta em branco no formulário de hoje. Então a LISTA
     continua disponível o tempo todo; o que mudou é o tom e o alarde. Antes da primeira
     tentativa ela é um resumo recolhido do que ainda falta, e nenhum campo nasce vermelho.
     Depois da tentativa vira o que sempre foi: a recusa, aberta e apontando cada pergunta. */
  const impedimentos = congelada ? [] : formulario.impedimentos;
  /* O erro direto do servidor vence o impedimento: ele é a resposta ao último ato da pessoa,
     enquanto o impedimento é o retrato do formulário inteiro. */
  /* O corte não é "todo impedimento espera a tentativa": é o motivo que decide. Pergunta em
     BRANCO é estado neutro — a pessoa não errou, ela acabou de chegar, e marcá-la de vermelho na
     abertura é o que fazia o formulário parecer quebrado. Já "prometeu anexo e não anexou" e
     "resposta inválida" são consequência de um ato dela: aparecem na hora, porque aí o vermelho
     significa o que deve significar. */
  const erros: Record<string, string> = {
    ...Object.fromEntries(
      impedimentos
        .filter(
          (impedimento) => tentouEnviar || impedimento.motivo !== "obrigatoria_sem_resposta",
        )
        .map((impedimento) => [impedimento.pergunta_id, impedimento.detalhe]),
    ),
    ...errosDoServidor,
  };
  const falhaDeAnexo = anexar.error ?? remover.error;
  /* A recusa do anexo pousa NA PERGUNTA que a causou. Ela nascia só na faixa do topo, e a prova
     em produção mediu a distância: 2.269 px acima do botão que a pessoa acabou de clicar, numa
     janela de 720 px. O texto estava certo ("Formato não aceito nesta pergunta") e ninguém o
     via — do lado de cá da tela, escolher um arquivo do formato errado não fazia nada.
     É a mesma regra que a recusa de campo já segue nesta tela. */
  const perguntaDaFalhaDeAnexo = anexar.error
    ? (anexar.variables?.perguntaId ?? null)
    : remover.error
      ? (formulario?.perguntas.find((pergunta) =>
          pergunta.anexos.some((anexo) => anexo.id === remover.variables),
        )?.id ?? null)
      : null;
  /* A recusa que NÃO cabe em nenhum campo. `errosPorPergunta` só sabe ler `detalhe.perguntas` e
     `detalhe.impedimentos`; um 409 ("esta trilha já foi enviada"), um 500 ou uma queda de rede
     vêm sem `detalhe` e sumiam por completo — a pessoa continuava digitando numa trilha que o
     servidor já tinha fechado, sem uma palavra na tela. O caminho é o do gate de aceite: duas
     pessoas na mesma trilha, uma envia, a outra segue digitando.
     A faixa só aparece quando a recusa não pousou em nenhum campo — repetir no topo o que já
     está no campo é a faixa genérica que esta tela existe para não ter. */
  const recusaDoServidor = salvar.error ?? enviar.error;
  const recusaSemCampo =
    recusaDoServidor && Object.keys(errosDoServidor).length === 0 ? recusaDoServidor : null;
  /* A faixa do topo fica com o que NÃO coube em pergunta nenhuma. Repetir lá o que já está na
     pergunta é justamente a faixa genérica que esta tela existe para não ter. */
  const falhaDeAnexoOuRecusa = (perguntaDaFalhaDeAnexo ? null : falhaDeAnexo) ?? recusaSemCampo;
  /* Três estados, e o do meio é o que a promessa da tela depende: enquanto houver coisa não
     gravada, o rascunho não está "salvo" — o relógio do salvamento automático está a caminho. */
  const estadoDoRascunho = pausado
    ? "erro"
    : salvando || pendentes.size > 0
      ? "salvando"
      : "salvo";

  return (
    <>
      <div className="cabecalho-da-trilha">
        <div>
          <h1 className="titulo-pagina">{formulario.nome}</h1>
          <p className="lead mb-2">{formulario.origem}</p>
        </div>
        <Chip
          estado={CHIP_DO_ESTADO[formulario.sessao.estado]}
          data-testid="estado-da-trilha"
        >
          {ROTULO_DO_ESTADO[formulario.sessao.estado]}
        </Chip>
      </div>

      <BarraDeProgresso progresso={formulario.progresso} />

      {congelada ? (
        <Aviso tom="sucesso" className="mt-4" data-testid="trilha-enviada">
          <b>Respostas enviadas{formularioEnviadoEm(formulario)}.</b> O formulário está fechado
          para alteração. {formulario.pode_anexar ? "Você pode acrescentar arquivos às perguntas abaixo. " : ""}
          Se alguma resposta mudou, avise o consultor responsável pela sua implantação — a
          correção entra pelo acompanhamento, não por aqui.
        </Aviso>
      ) : somenteLeitura ? (
        <Aviso tom="atencao" className="mt-4" data-testid="somente-leitura">
          <b>Você pode ver este formulário, mas não respondê-lo.</b> Peça a permissão a quem
          administra o acesso da sua organização.
        </Aviso>
      ) : null}

      {impedimentos.length > 0 ? (
        <Aviso
          tom={tentouEnviar ? "erro" : "neutro"}
          className="mt-4"
          data-testid="impedimentos"
          data-tentou-enviar={tentouEnviar ? "sim" : "nao"}
        >
          {/* Recolhido antes da tentativa e aberto depois: a mesma informação, no volume que o
              momento pede. `open` é atributo do DOM, então o React o refaz quando `tentouEnviar`
              muda — é o que abre o painel no clique do envio recusado. */}
          <details className="impedimentos" open={tentouEnviar}>
            <summary>
              <b>
                {tentouEnviar
                  ? impedimentos.length === 1
                    ? "Falta 1 item para enviar."
                    : `Faltam ${impedimentos.length} itens para enviar.`
                  : impedimentos.length === 1
                    ? "Ainda falta 1 pergunta."
                    : `Ainda faltam ${impedimentos.length} perguntas.`}
              </b>
            </summary>
            <ul className="lista-de-impedimentos">
              {impedimentos.map((impedimento) => (
                <li key={impedimento.pergunta_id}>
                  <a href={`#${ancoraDaPergunta(impedimento.pergunta_id)}`}>
                    {impedimento.enunciado}
                  </a>{" "}
                  — {ROTULO_DO_MOTIVO[impedimento.motivo]}
                </li>
              ))}
            </ul>
          </details>
        </Aviso>
      ) : null}

      {falhaDeAnexoOuRecusa ? (
        <Aviso tom="erro" className="mt-4" data-testid="falha-de-anexo">
          {falhaDeAnexoOuRecusa instanceof ErroDaApi
            ? falhaDeAnexoOuRecusa.message
            : "Não foi possível falar com o servidor agora. Tente de novo em instantes."}
        </Aviso>
      ) : null}

      <form
        className="formulario-da-trilha"
        data-testid="formulario-da-trilha"
        data-chave={formulario.chave}
        onSubmit={(evento) => evento.preventDefault()}
        /* Sair de um campo grava na hora. É o que faz "pode parar e voltar" valer também para
           quem fecha a aba antes de o relógio do salvamento automático disparar. */
        onBlur={() => {
          if (!pausado && !salvando && pendentes.size > 0) void gravar(pendentes);
        }}
      >
        {formulario.perguntas.map((pergunta) => (
          <PerguntaNoFormulario
            key={pergunta.id}
            pergunta={pergunta}
            valor={
              pendentes.has(pergunta.id)
                ? (pendentes.get(pergunta.id) ?? null)
                : (pergunta.resposta?.valor ?? null)
            }
            erro={erros[pergunta.id]}
            falhaDoAnexo={
              perguntaDaFalhaDeAnexo === pergunta.id && falhaDeAnexo
                ? falhaDeAnexo instanceof ErroDaApi
                  ? falhaDeAnexo.message
                  : "Não foi possível falar com o servidor agora. Tente de novo em instantes."
                : undefined
            }
            somenteLeitura={somenteLeitura}
            podeAnexar={formulario.pode_anexar ?? formulario.pode_responder}
            ocupado={ocupado}
            nomeDaOrigem={
              pergunta.resposta?.herdada_de_trilha
                ? nomesDasTrilhas[pergunta.resposta.herdada_de_trilha]
                : undefined
            }
            aoMudar={(valor) => aoMudar(pergunta.id, valor)}
            aoAnexar={(arquivo) => anexar.mutate({ perguntaId: pergunta.id, arquivo })}
            aoRemoverAnexo={(anexoId) => remover.mutate(anexoId)}
          />
        ))}
      </form>

      {somenteLeitura ? (
        /* Em modo leitura a barra inteira sumia — e levava junto a única saída da tela. Quem
           acabou de enviar a trilha ficava sem caminho de volta para a lista de formulários. */
        <div className="barra-de-acoes">
          <Link to={CAMINHO_DOS_FORMULARIOS} className="botao botao--fantasma">
            Voltar aos formulários
          </Link>
        </div>
      ) : (
        <div className="barra-de-acoes">
          <p
            className="barra-de-acoes__aviso"
            role="status"
            data-testid="estado-do-rascunho"
            data-estado={estadoDoRascunho}
          >
            {salvando
              ? "Salvando…"
              : pausado
                ? /* O relógio do salvamento automático está PARADO — ele só volta quando a
                     pessoa mexer em algo. Dizer "são gravadas sozinhas em instantes" aqui era
                     mentira sobre a única promessa que esta tela faz. */
                  "Não foi possível salvar. Corrija o que está indicado ou use “Salvar agora”."
                : pendentes.size > 0
                  ? "Alterações ainda não salvas — elas são gravadas sozinhas em instantes."
                  : "Tudo salvo. Você pode fechar e voltar depois."}
          </p>
          {pendentes.size > 0 ? (
            <Botao
              variante="fantasma"
              disabled={salvando}
              onClick={() => void gravar(pendentes)}
            >
              Salvar agora
            </Botao>
          ) : null}
          <Link to={CAMINHO_DOS_FORMULARIOS} className="botao botao--fantasma">
            Voltar aos formulários
          </Link>
          {/* O botão deixou de nascer travado: um botão desabilitado sem explicação é um beco, e
              travá-lo para sempre tornava impossível existir uma "primeira tentativa" — o gate
              que faz o vermelho aparecer só depois de a pessoa tentar. O clique com pendência
              NÃO chama o servidor: revela a lista aqui mesmo e leva até ela. A trilha continua
              sem congelar por engano, que era o motivo original da trava. */}
          <Botao
            variante="verde"
            disabled={ocupado}
            data-testid="enviar-trilha"
            onClick={() => {
              if (impedimentos.length > 0) {
                setTentouEnviar(true);
                document
                  .querySelector('[data-testid="impedimentos"]')
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
                return;
              }
              setConfirmandoEnvio(true);
            }}
          >
            Enviar respostas
          </Botao>
        </div>
      )}

      {confirmandoEnvio ? (
        <Aviso tom="atencao" className="mt-3" data-testid="confirmar-envio">
          <b>Enviar fecha este formulário para alteração.</b> Depois disso não dá para voltar e
          editar: uma correção passa a depender de conversar com o consultor responsável.
          <span className="confirmacao__acoes">
            <Botao variante="fantasma" disabled={enviando} onClick={() => setConfirmandoEnvio(false)}>
              Cancelar
            </Botao>
            <Botao
              variante="verde"
              disabled={ocupado || enviando}
              data-testid="confirmar-envio-botao"
              onClick={() => void confirmarEnvio()}
            >
              {enviando ? "Enviando…" : "Confirmar envio"}
            </Botao>
          </span>
        </Aviso>
      ) : null}
    </>
  );
}

function formularioEnviadoEm(formulario: FormularioDaTrilha): string {
  const quando = formatarData(formulario.sessao.enviada_em);
  return quando ? ` em ${quando}` : "";
}
